import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, ProgressBar, Modal, Collapse, Row, Col } from 'react-bootstrap';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';

const Evaluations = () => {
  const [stats, setStats] = useState({ total: 0, synced: 0, percentage: 0 });
  const [evaluations, setEvaluations] = useState([]);
  const [expandedEvaluation, setExpandedEvaluation] = useState(null);
  const [grades, setGrades] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ show: false, current: 0, total: 0 });

  useEffect(() => {
    loadStats();
    loadEvaluations();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/evaluations/stats');
      const evalStats = response.data.Evaluation || { total: 0, synced: 0, percentage: 0 };
      setStats(evalStats);
    } catch (error) {
      showToast('Error cargando estadísticas', 'danger');
    }
  };

  const loadEvaluations = async () => {
    try {
      const response = await api.get('/evaluations/all');
      setEvaluations(response.data.evaluations || []);
    } catch (error) {
      console.error('Error loading evaluations:', error);
    }
  };

  const loadEvaluationGrades = async (evaluationId) => {
    try {
      const response = await api.get(`/evaluations/${evaluationId}/grades`);
      setGrades(prev => ({
        ...prev,
        [evaluationId]: response.data.grades || []
      }));
      setExpandedEvaluation(evaluationId);
    } catch (error) {
      showToast('Error cargando notas de la evaluación', 'danger');
      setGrades(prev => ({ ...prev, [evaluationId]: [] }));
    }
  };

  const migrateEvaluations = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    const totalItems = stats.total - stats.synced;
    setMigrationProgress({ show: true, current: 0, total: totalItems });
    
    try {
      let hasMore = true;
      let totalProcessed = 0;
      
      while (hasMore) {
        const response = await api.post('/evaluations/migrate', { batchSize: 5 });
        
        totalProcessed += response.data.processed;
        hasMore = response.data.hasMore;
        
        setMigrationProgress(prev => ({ ...prev, current: totalProcessed }));
        
        // Mostrar errores si los hay
        if (response.data.errors && response.data.errors.length > 0) {
          console.error('Migration errors:', response.data.errors);
          showToast(`Errores en migración: ${response.data.errors.length} elementos fallaron`, 'warning');
          break; // Detener si hay errores
        }
        
        if (response.data.processed === 0) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      showToast(`Evaluaciones migradas: ${totalProcessed}`, 'success');
      await loadStats();
      await loadEvaluations();
    } catch (error) {
      showToast('Error migrando evaluaciones', 'danger');
    } finally {
      setIsProcessing(false);
      setMigrationProgress({ show: false, current: 0, total: 0 });
    }
  };

  const getProgressPercentage = (synced, total) => total > 0 ? Math.round((synced / total) * 100) : 0;
  const percentage = getProgressPercentage(stats.synced, stats.total);
  const pending = stats.total - stats.synced;

  const EvaluationRow = ({ evaluation }) => {
    const isExpanded = expandedEvaluation === evaluation.id;
    const evaluationGrades = grades[evaluation.id] || [];
    
    return (
      <>
        <tr>
          <td>
            <div className="d-flex align-items-center">
              <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '35px', height: '35px'}}>
                <i className="bi bi-clipboard-check"></i>
              </div>
              <div>
                <div className="fw-medium">{evaluation.name}</div>
                <small className="text-muted">ID: {evaluation.id}</small>
              </div>
            </div>
          </td>
          <td>
            <span className="text-muted">{evaluation.subject_name}</span>
            <br />
            <small className="text-muted">{evaluation.section_name}</small>
          </td>
          <td>
            <Badge bg="secondary" className="small">{evaluation.period_name}</Badge>
          </td>
          <td>
            {evaluation.lms_id ? (
              <Badge bg="success">Migrado</Badge>
            ) : (
              <Badge bg="warning">Pendiente</Badge>
            )}
          </td>
          <td>
            {evaluation.lms_id && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => isExpanded ? setExpandedEvaluation(null) : loadEvaluationGrades(evaluation.id)}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                {isExpanded ? 'Ocultar' : 'Ver'} Notas
              </Button>
            )}
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan="5" className="p-0">
              <Collapse in={isExpanded}>
                <div className="border-top p-3">
                  <h6 className="mb-3">
                    <i className="bi bi-graph-up me-2"></i>
                    Notas de la Evaluación ({evaluationGrades.length})
                  </h6>
                  {evaluationGrades.length > 0 ? (
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Estudiante</th>
                            <th>Nota</th>
                            <th>Estado</th>
                            <th>Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evaluationGrades.map((grade, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-person text-primary me-2"></i>
                                  <span className="small">{grade.firstname} {grade.lastname}</span>
                                </div>
                              </td>
                              <td>
                                <Badge 
                                  bg={grade.grade >= 70 ? 'success' : grade.grade >= 60 ? 'warning' : 'danger'}
                                  className="small"
                                >
                                  {grade.grade || 'Sin nota'}
                                </Badge>
                              </td>
                              <td>
                                <Badge 
                                  bg={grade.status === 'submitted' ? 'info' : 'secondary'}
                                  className="small"
                                >
                                  {grade.status || 'Pendiente'}
                                </Badge>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {grade.timemodified ? new Date(grade.timemodified * 1000).toLocaleDateString() : '-'}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <i className="bi bi-clipboard-x fs-1 text-muted opacity-25"></i>
                      <p className="text-muted mt-2 mb-0">No hay notas registradas</p>
                    </div>
                  )}
                </div>
              </Collapse>
            </td>
          </tr>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestión de Evaluaciones</h2>
        <div className="d-flex gap-2">
          <Badge bg="success">{stats.synced} Migradas</Badge>
          <Badge bg="warning">{pending} Pendientes</Badge>
        </div>
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Progreso de Migración</h5>
              <small className="text-muted">Evaluaciones marcadas para Moodle</small>
            </div>
            <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '60px', height: '60px'}}>
              <i className="bi bi-clipboard-check fs-4"></i>
            </div>
          </div>
          
          <ProgressBar now={percentage} variant="info" className="mb-3" style={{height: '12px'}} />
          
          <div className="row text-center mb-4">
            <div className="col-4">
              <div className="h4 text-success mb-0">{stats.synced}</div>
              <small className="text-muted">Migradas</small>
            </div>
            <div className="col-4">
              <div className="h4 text-warning mb-0">{pending}</div>
              <small className="text-muted">Pendientes</small>
            </div>
            <div className="col-4">
              <div className="h4 text-secondary mb-0">{stats.total}</div>
              <small className="text-muted">Total</small>
            </div>
          </div>
          
          <Button 
            variant={pending > 0 ? 'info' : 'outline-secondary'}
            className="w-100"
            onClick={migrateEvaluations}
            disabled={isProcessing || pending === 0}
          >
            <i className="bi bi-cloud-upload me-1"></i>
            {pending === 0 ? 'Todas las evaluaciones migradas' : 
             isProcessing ? 'Migrando evaluaciones...' : `Migrar ${pending} evaluaciones`}
          </Button>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-info text-white">
          <h6 className="mb-0">
            <i className="bi bi-clipboard-check me-2"></i>
            Evaluaciones ({evaluations.length})
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Evaluación</th>
                <th>Materia</th>
                <th>Período</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {evaluations.map((evaluation, index) => (
                <EvaluationRow key={index} evaluation={evaluation} />
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={migrationProgress.show} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <div className="spinner-border text-info mb-3" role="status">
              <span className="visually-hidden">Migrando...</span>
            </div>
            <h5>Migrando Evaluaciones</h5>
            <p className="text-muted mb-0">
              {migrationProgress.current} de {migrationProgress.total} evaluaciones procesadas
            </p>
          </div>
          
          <ProgressBar 
            now={migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}
            variant="info"
            className="mb-3"
            style={{height: '10px'}}
          />
          
          <div className="text-muted">
            {migrationProgress.total > 0 ? Math.round((migrationProgress.current / migrationProgress.total) * 100) : 0}% completado
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Evaluations;